import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { MessageCircle, X, Send, Image as ImageIcon } from 'react-feather';
import chatAPI, {
  ChatAttachment,
  ChatConversation,
  ChatMessage,
  resolveChatMediaUrl,
} from '../services/chatApi';
import SecureImage from './SecureImage';
import useChatSocket from '../hooks/useChatSocket';
import { getImageFileFromClipboard } from '../utils/chatClipboardImage';
import '../styles/components/_chat-widget.scss';

const formatDayLabel = (dateStr: string, t: (k: string) => string): string => {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return t('chat.today');
  if (d.toDateString() === yesterday.toDateString()) return t('chat.yesterday');
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
};

const formatTime = (dateStr: string) =>
  new Date(dateStr).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

const sortMessagesByDate = (items: ChatMessage[]): ChatMessage[] =>
  [...items].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

const mergeMessages = (prev: ChatMessage[], incoming: ChatMessage): ChatMessage[] => {
  if (prev.some((m) => m._id === incoming._id)) return prev;
  return sortMessagesByDate([...prev, incoming]);
};

type PendingAttachment = {
  file: File;
  localPreview: string;
};

const revokePendingAttachment = (att: PendingAttachment) => {
  URL.revokeObjectURL(att.localPreview);
};

const parseRouteContext = (pathname: string) => {
  const courseMatch = pathname.match(/^\/course\/([^/]+)/);
  const courseId = courseMatch?.[1];
  let itemType: string | undefined;
  let chapterId: string | undefined;
  let itemId: string | undefined;

  const capsule = pathname.match(/chapter\/([^/]+)\/capsule\/([^/]+)/);
  const quiz = pathname.match(/chapter\/([^/]+)\/quiz\/([^/]+)/);
  const workshop = pathname.match(/chapter\/([^/]+)\/workshop\/([^/]+)/);
  const assessment = pathname.match(/assessment(?:\/followup\/([^/]+)|\/([^/]+))?/);

  if (capsule) {
    itemType = 'capsule';
    chapterId = capsule[1];
    itemId = capsule[2];
  } else if (quiz) {
    itemType = 'quiz';
    chapterId = quiz[1];
    itemId = quiz[2];
  } else if (workshop) {
    itemType = 'workshop';
    chapterId = workshop[1];
    itemId = workshop[2];
  } else if (assessment) {
    itemType = 'assessment';
    itemId = assessment[1] || assessment[2];
  }

  return { courseId, itemType, chapterId, itemId, path: pathname };
};

const MIN_THREAD_SPINNER_MS = 400;
const MESSAGE_PREVIEW_DURATION_MS = 5000;

const getMessagePreviewText = (
  msg: ChatMessage,
  t: (key: string) => string
): string => {
  const trimmed = msg.content?.trim();
  if (trimmed) {
    return trimmed.length > 120 ? `${trimmed.slice(0, 120)}…` : trimmed;
  }
  if (msg.attachments?.length) return t('chat.imageMessage');
  return t('chat.newMessage');
};

const requestNotificationPermission = async () => {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission === 'default') {
    await Notification.requestPermission();
  }
};

const showBrowserNotification = (title: string, body: string) => {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  if (!document.hidden) return;
  try {
    new Notification(title, { body, icon: `${process.env.PUBLIC_URL || ''}/logo-default.png` });
  } catch {
    // ignore
  }
};

const ChatWidget: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const params = useParams<{ courseId?: string }>();
  const token = localStorage.getItem('student_token');
  const uiLanguage = i18n.language.split('-')[0] || 'fr';

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [threadReady, setThreadReady] = useState(false);
  const [sending, setSending] = useState(false);
  const [conversation, setConversation] = useState<ChatConversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [draft, setDraft] = useState('');
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [typing, setTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messagePreview, setMessagePreview] = useState<string | null>(null);

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const conversationIdRef = useRef<string | null>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typingClearRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messagePreviewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    conversationIdRef.current = conversation?._id ?? null;
  }, [conversation?._id]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior });
  }, []);

  const loadConversation = useCallback(async () => {
    setLoading(true);
    setThreadReady(false);
    setError(null);
    const startedAt = Date.now();
    try {
      const data = await chatAPI.getConversation(uiLanguage);
      setConversation(data.conversation);
      setMessages(sortMessagesByDate(data.messages));
      setUnreadCount(data.conversation.unreadByStudent || 0);
    } catch {
      setError(t('chat.loadError'));
      setThreadReady(true);
    } finally {
      const elapsed = Date.now() - startedAt;
      if (elapsed < MIN_THREAD_SPINNER_MS) {
        await new Promise((resolve) => setTimeout(resolve, MIN_THREAD_SPINNER_MS - elapsed));
      }
      setLoading(false);
    }
  }, [t, uiLanguage]);

  const handleMessageImageLoaded = useCallback(() => {
    if (!open || !threadReady) return;
    scrollToBottom('auto');
  }, [open, threadReady, scrollToBottom]);

  const handleToggleOpen = () => {
    if (open) {
      setOpen(false);
      return;
    }
    setMessagePreview(null);
    if (messagePreviewTimeoutRef.current) {
      clearTimeout(messagePreviewTimeoutRef.current);
      messagePreviewTimeoutRef.current = null;
    }
    setMessages([]);
    setLoading(true);
    setThreadReady(false);
    setOpen(true);
  };

  const hideMessagePreview = useCallback(() => {
    if (messagePreviewTimeoutRef.current) {
      clearTimeout(messagePreviewTimeoutRef.current);
      messagePreviewTimeoutRef.current = null;
    }
    setMessagePreview(null);
  }, []);

  const showMessagePreview = useCallback(
    (text: string) => {
      setMessagePreview(text);
      if (messagePreviewTimeoutRef.current) {
        clearTimeout(messagePreviewTimeoutRef.current);
      }
      messagePreviewTimeoutRef.current = setTimeout(
        hideMessagePreview,
        MESSAGE_PREVIEW_DURATION_MS
      );
    },
    [hideMessagePreview]
  );

  useEffect(() => {
    if (open) hideMessagePreview();
  }, [open, hideMessagePreview]);

  useEffect(
    () => () => {
      hideMessagePreview();
    },
    [hideMessagePreview]
  );

  const loadUnread = useCallback(async () => {
    try {
      const count = await chatAPI.getUnreadCount(uiLanguage);
      setUnreadCount(count);
    } catch {
      // ignore
    }
  }, [uiLanguage]);

  useEffect(() => {
    if (!token) return;
    loadUnread();
    requestNotificationPermission();
  }, [token, loadUnread]);

  useEffect(() => {
    if (open && token) {
      loadConversation();
    }
  }, [open, token, loadConversation]);

  useEffect(() => {
    if (open && conversation?._id) {
      chatAPI.markRead(conversation._id).catch(() => undefined);
      setUnreadCount(0);
    }
  }, [open, conversation?._id]);

  useEffect(() => {
    if (!open || loading || threadReady) return;

    let cancelled = false;
    const revealThread = () => {
      if (cancelled) return;
      scrollToBottom('auto');
      requestAnimationFrame(() => {
        if (cancelled) return;
        scrollToBottom('auto');
        setThreadReady(true);
      });
    };

    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(revealThread);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [open, loading, threadReady, messages, scrollToBottom]);

  useEffect(() => {
    if (!open || loading || !threadReady) return;
    scrollToBottom('smooth');
  }, [messages, typing, open, loading, threadReady, scrollToBottom]);

  const routeContext = useMemo(() => parseRouteContext(location.pathname), [location.pathname]);

  useEffect(() => {
    if (!token) return;
    const courseId = params.courseId || routeContext.courseId;

    chatAPI
      .updateContext({
        courseId: courseId || undefined,
        uiLanguage,
        item: courseId
          ? {
              chapterId: routeContext.chapterId,
              itemId: routeContext.itemId,
              itemType: routeContext.itemType,
              path: routeContext.path,
            }
          : undefined,
      })
      .catch(() => undefined);
  }, [token, params.courseId, routeContext, location.pathname, uiLanguage]);

  const handleIncomingMessage = useCallback(
    (payload: { conversationId: string; message: ChatMessage }) => {
      const msg = payload.message;
      const currentId = conversationIdRef.current;
      if (currentId && payload.conversationId !== currentId) return;

      setMessages((prev) => mergeMessages(prev, msg));

      if (msg.senderRole === 'admin') {
        if (!open) {
          setUnreadCount((c) => c + 1);
          showMessagePreview(getMessagePreviewText(msg, t));
          showBrowserNotification(t('chat.notificationTitle'), msg.content || t('chat.newMessage'));
        } else {
          chatAPI.markRead(payload.conversationId).catch(() => undefined);
        }
      }
    },
    [open, showMessagePreview, t]
  );

  const handleConversationUpdated = useCallback(
    (payload: { conversation: ChatConversation }) => {
      setConversation(payload.conversation);
      if (!open) {
        setUnreadCount(payload.conversation.unreadByStudent || 0);
      }
    },
    [open]
  );

  const handleTyping = useCallback(
    (payload: { conversationId: string; role: string; isTyping: boolean }) => {
      if (!conversation || payload.conversationId !== conversation._id) return;
      if (payload.role === 'admin') {
        if (payload.isTyping) {
          setTyping(true);
          if (typingClearRef.current) clearTimeout(typingClearRef.current);
          typingClearRef.current = setTimeout(() => setTyping(false), 3000);
        } else {
          setTyping(false);
        }
      }
    },
    [conversation]
  );

  const { emitTyping, joinConversation, leaveConversation } = useChatSocket(
    token,
    {
      onMessage: handleIncomingMessage as any,
      onConversationUpdated: handleConversationUpdated as any,
      onTyping: handleTyping,
    },
    Boolean(token)
  );

  useEffect(() => {
    if (!conversation?._id || !open) return;
    joinConversation(conversation._id);
    return () => leaveConversation(conversation._id);
  }, [conversation?._id, open, joinConversation, leaveConversation]);

  const handleDraftChange = (value: string) => {
    setDraft(value);
    if (!conversation?._id) return;
    emitTyping(conversation._id, true);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      emitTyping(conversation._id, false);
    }, 1200);
  };

  const addPendingImage = (file: File) => {
    if (!file.type.startsWith('image/') || attachments.length >= 4) return;
    setAttachments((prev) => [
      ...prev,
      { file, localPreview: URL.createObjectURL(file) },
    ]);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    addPendingImage(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    if (uploading || sending || attachments.length >= 4) return;
    const file = getImageFileFromClipboard(e);
    if (!file) return;
    e.preventDefault();
    addPendingImage(file);
  };

  const handleSend = async () => {
    if ((!draft.trim() && attachments.length === 0) || sending) return;
    setSending(true);
    setError(null);
    try {
      setUploading(true);
      const uploadedAttachments: ChatAttachment[] = [];
      for (const att of attachments) {
        uploadedAttachments.push(await chatAPI.uploadImage(att.file));
      }

      const courseId = params.courseId || routeContext.courseId;
      const result = await chatAPI.sendMessage(
        draft.trim(),
        uploadedAttachments,
        courseId
          ? {
              courseId,
              item: {
                chapterId: routeContext.chapterId,
                itemId: routeContext.itemId,
                itemType: routeContext.itemType,
                path: routeContext.path,
              },
            }
          : undefined,
        uiLanguage
      );
      attachments.forEach(revokePendingAttachment);
      setDraft('');
      setAttachments([]);
      setConversation(result.conversation);
      setMessages((prev) => mergeMessages(prev, result.message));
      if (conversation?._id) emitTyping(conversation._id, false);
    } catch {
      setError(t('chat.sendError'));
    } finally {
      setUploading(false);
      setSending(false);
    }
  };

  const groupedMessages = useMemo(() => {
    const groups: { day: string; items: ChatMessage[] }[] = [];
    messages.forEach((msg) => {
      const day = formatDayLabel(msg.createdAt, t);
      const last = groups[groups.length - 1];
      if (last?.day === day) last.items.push(msg);
      else groups.push({ day, items: [msg] });
    });
    return groups;
  }, [messages, t]);

  if (!token) return null;

  return (
    <Box className="chat-widget">
      {open && (
        <Box className="chat-widget__panel">
          <Box className="chat-widget__header">
            <Typography className="chat-widget__title">{t('chat.title')}</Typography>
            <IconButton size="small" onClick={() => setOpen(false)} aria-label={t('chat.close')}>
              <X size={18} />
            </IconButton>
          </Box>

          <Box ref={messagesContainerRef} className="chat-widget__messages">
            {!threadReady && (
              <Box className="chat-widget__loading-overlay">
                <CircularProgress size={28} />
              </Box>
            )}
            <Box
              className={`chat-widget__messages-content${
                threadReady ? '' : ' chat-widget__messages-content--hidden'
              }`}
            >
              {groupedMessages.map((group) => (
                <Box key={group.day}>
                  <Typography className="chat-widget__day-label">{group.day}</Typography>
                  {group.items.map((msg) => {
                    const isMine = msg.senderRole === 'student';
                    const isSystem = msg.senderRole === 'system';
                    return (
                      <Box
                        key={msg._id}
                        className={`chat-widget__bubble-row ${
                          isMine ? 'chat-widget__bubble-row--mine' : ''
                        } ${isSystem ? 'chat-widget__bubble-row--system' : ''}`}
                      >
                        <Box
                          className={`chat-widget__bubble ${
                            isMine
                              ? 'chat-widget__bubble--mine'
                              : isSystem
                              ? 'chat-widget__bubble--system'
                              : 'chat-widget__bubble--theirs'
                          }`}
                        >
                          {msg.content && (
                            <Typography className="chat-widget__text">{msg.content}</Typography>
                          )}
                          {msg.attachments?.map((att, i) => (
                            <a
                              key={i}
                              className="chat-widget__image-link"
                              href={resolveChatMediaUrl(att.url)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <SecureImage
                                srcPath={att.url}
                                alt={att.filename}
                                className="chat-widget__image"
                                fixedWidth={220}
                                fixedHeight={160}
                                borderRadius={8}
                                showLoader
                                showZoomHint
                                onLoad={handleMessageImageLoaded}
                              />
                            </a>
                          ))}
                          <Typography className="chat-widget__time">{formatTime(msg.createdAt)}</Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ))}
              {typing && (
                <Typography className="chat-widget__typing">{t('chat.typing')}</Typography>
              )}
              <div ref={messagesEndRef} />
            </Box>
          </Box>

          {attachments.length > 0 && (
            <Box className="chat-widget__attachments-preview">
              {attachments.map((att, i) => (
                <Box key={i} className="chat-widget__attachment-thumb">
                  <img src={att.localPreview} alt="" />
                  <IconButton
                    size="small"
                    className="chat-widget__remove-attachment"
                    onClick={() => {
                      setAttachments((prev) => {
                        const removed = prev[i];
                        if (removed) revokePendingAttachment(removed);
                        return prev.filter((_, idx) => idx !== i);
                      });
                    }}
                  >
                    <X size={12} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          {error && (
            <Typography className="chat-widget__error" color="error">
              {error}
            </Typography>
          )}

          <Box className="chat-widget__composer">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleImageSelect}
            />
            <IconButton
              size="small"
              className="chat-widget__action-btn chat-widget__action-btn--secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || sending || attachments.length >= 4}
              aria-label={t('chat.attachImage')}
            >
              {uploading ? <CircularProgress size={16} sx={{ color: 'var(--brand-shell-bg, #13322B)' }} /> : <ImageIcon size={18} />}
            </IconButton>
            <TextField
              fullWidth
              size="small"
              multiline
              maxRows={4}
              placeholder={t('chat.placeholder')}
              value={draft}
              onChange={(e) => handleDraftChange(e.target.value)}
              onPaste={handlePaste}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              className="chat-widget__input"
              slotProps={{
                input: {
                  sx: { color: 'var(--brand-shell-bg, #13322B)' },
                },
              }}
            />
            <IconButton
              className="chat-widget__action-btn"
              onClick={handleSend}
              disabled={sending || uploading || (!draft.trim() && attachments.length === 0)}
              aria-label={t('chat.send')}
            >
              <Send size={18} />
            </IconButton>
          </Box>
        </Box>
      )}

      <Box className="chat-widget__fab-stack">
        {!open && messagePreview && (
          <Box
            className="chat-widget__message-preview"
            onClick={handleToggleOpen}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggleOpen();
              }
            }}
            aria-label={t('chat.open')}
          >
            <Typography className="chat-widget__message-preview-title">
              {t('chat.notificationTitle')}
            </Typography>
            <Typography className="chat-widget__message-preview-text">{messagePreview}</Typography>
          </Box>
        )}

        <IconButton
          className="chat-widget__fab"
          onClick={handleToggleOpen}
          aria-label={t('chat.open')}
        >
          <Badge badgeContent={unreadCount > 0 ? unreadCount : undefined} color="error">
            <MessageCircle size={24} />
          </Badge>
        </IconButton>
      </Box>
    </Box>
  );
};

export default ChatWidget;
